import { useEffect, useMemo, useRef, useState } from 'react';
import { Palette } from './Palette';
import { useWindows, type WindowID } from './Windows';
import { Column, Font, Row } from './DesignSystem';
import { EnableTypographyPalette } from './TypographyPreview';
import { EnableScreenPalette } from './ScreenPreview';

export type QuestStep = {
  name: string;
  windowID: Exclude<WindowID, 'codi'>;
};

export type QuestData = {
  name: string;
  steps: QuestStep[];
};

export function OOBEQuest(): QuestData {
  const steps: QuestStep[] = [
    { name: 'Choose your colors', windowID: 'colors' },
  ];

  if (EnableTypographyPalette) {
    steps.push({ name: 'Choose your fonts', windowID: 'typography' });
  }

  if (EnableScreenPalette) {
    steps.push({ name: 'Choose your screens', windowID: 'screens' });
  }

  return { name: 'Lay your foundations', steps };
}

export const EnableQuests = false;
export function Quest({ name, steps }: QuestData) {
  const [stepIndex, setStepIndex] = useState<number>(0);
  const { state: windowState, dispatch: windowDispatch } = useWindows();
  const previousStepIndexRef = useRef<number | null>(null);
  const previousStepSignatureRef = useRef<string | null>(null);
  const stepSignature = useMemo(
    () => steps.map(({ windowID }) => windowID).join('|'),
    [steps],
  );

  function isActiveStep(i: number): boolean {
    return i === stepIndex;
  }

  useEffect(() => {
    if (!EnableQuests) {
      return;
    }

    const stepChanged = previousStepIndexRef.current !== stepIndex;
    const signatureChanged = previousStepSignatureRef.current !== stepSignature;

    if (!stepChanged && !signatureChanged) {
      return;
    }

    previousStepIndexRef.current = stepIndex;
    previousStepSignatureRef.current = stepSignature;

    const activeStep = steps[stepIndex];
    const activeWindow = activeStep
      ? windowState.windows[activeStep.windowID]
      : undefined;
    if (activeStep !== undefined && activeWindow !== undefined) {
      windowDispatch({ type: 'show', id: activeStep.windowID });
      windowDispatch({ type: 'move-codi', id: activeStep.windowID });
      windowDispatch({ type: 'center', id: 'codi' });
    }

    steps.forEach((step, i) => {
      if (i !== stepIndex && windowState.windows[step.windowID]) {
        windowDispatch({ type: 'hide', id: step.windowID });
      }
    });
  }, [stepIndex, steps, stepSignature, windowDispatch, windowState.windows]);

  if (!EnableQuests) {
    return <></>;
  }

  return (
    <Palette title={name} windowID="quest">
      <Column>
        {steps.map((step, i) => {
          const stepIndicatorBackgroundColor = isActiveStep(i)
            ? '#174DAC'
            : '#174DAC66';
          return (
            <Row
              key={step.name}
              style={{ alignItems: 'center', marginBlockEnd: '8px' }}
              onClick={() => {
                setStepIndex(i);
              }}
            >
              <Row
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '16px',
                  backgroundColor: stepIndicatorBackgroundColor,
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginInlineEnd: '8px',
                }}
              >
                <Font kind="font-body" style={{ color: 'white' }}>
                  {i + 1}
                </Font>
              </Row>
              <Font kind="font-body">{step.name}</Font>
            </Row>
          );
        })}
      </Column>
    </Palette>
  );
}
